import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { RiskTrendPoint } from "../../../utils/hooks/dashboard/useExecutiveSummaryData";
import { getMonthlyRiskTrend } from "../../../utils/api/endpoints/dashboard/dashboardApi";
import { FiLoader } from "react-icons/fi";
import { MdInfo } from "react-icons/md";
import ChartFilter, { getPeriodLabel, TimeRange } from "./ChartFilter";

interface RiskHeatmapProps {
  data: RiskTrendPoint[];
  isSubscriptionOnly?: boolean;
  subscriptionStartDate?: string | null;
  selectedYear?: number | null;
}

const RiskHeatmap: React.FC<RiskHeatmapProps> = ({ 
  data: initialData, 
  isSubscriptionOnly = false, 
  subscriptionStartDate, 
  selectedYear = null 
}) => {
  const [timeFilter, setTimeFilter] = useState<TimeRange>(
    (selectedYear !== null || isSubscriptionOnly) ? 'yearly' : 'all'
  );
  const [localData, setLocalData] = useState<RiskTrendPoint[]>(initialData);
  const [periodLabel, setPeriodLabel] = useState<string>('Yearly');
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialMount, setIsInitialMount] = useState(true);
  const [showInfo, setShowInfo] = useState(false);

  // Sync local timeFilter with global filters when they change
  useEffect(() => {
    if (selectedYear !== null || isSubscriptionOnly) {
      setTimeFilter('yearly');
    } else {
      setTimeFilter('all');
    }
  }, [selectedYear, isSubscriptionOnly]);

  const currentYear = new Date().getFullYear();
  const effectiveTimeFilter = (selectedYear && selectedYear !== currentYear) 
    ? 'yearly' 
    : timeFilter;

  useEffect(() => {
    setLocalData(initialData);
  }, [initialData]);

  useEffect(() => {
    if (isInitialMount) {
      setIsInitialMount(false);
      return;
    }

    let isMounted = true;
    const fetchFilteredData = async () => {
      setIsLoading(true);
      try {
        const response = await getMonthlyRiskTrend(effectiveTimeFilter, isSubscriptionOnly, selectedYear);
        if (isMounted && response?.success && response.data) {
          const fetchedData = response.data.data || response.data;
          setLocalData(fetchedData);
          if (response.data.periodInfo?.label) {
            setPeriodLabel(response.data.periodInfo.label);
          }
        }
      } catch (e) {
        console.error("Failed to fetch filtered risk trend", e);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchFilteredData();
    return () => { isMounted = false; };
  }, [effectiveTimeFilter, isSubscriptionOnly, selectedYear]);

  const getBgColor = (severity: string, value: number) => {
    if (value === 0) return 'bg-slate-50/50 text-slate-300';
    
    switch (severity) {
      case 'critical':
        return 'bg-rose-50 text-rose-600 border border-rose-100';
      case 'high':
        return 'bg-orange-50 text-orange-600 border border-orange-100';
      case 'medium':
        return 'bg-amber-50 text-amber-600 border border-amber-100';
      case 'low':
        return 'bg-emerald-50 text-emerald-600 border border-emerald-100';
      case 'info':
      default:
        return 'bg-slate-100 text-slate-600 border border-slate-200';
    }
  };

  const getHeaderColor = (severity: string) => {
    switch (severity) {
      case 'Critical': return 'text-rose-500';
      case 'High': return 'text-orange-500';
      case 'Medium': return 'text-amber-500';
      case 'Low': return 'text-emerald-500';
      case 'Info': return 'text-slate-500';
      default: return 'text-slate-500';
    }
  };

  const isCompact = timeFilter === 'yearly' || timeFilter === 'all';
  const spacingClass = isCompact ? 'border-spacing-y-1.5' : 'border-spacing-y-4';
  const cellPaddingClass = isCompact ? 'py-1.5' : 'py-3.5';
  const cellTextClass = isCompact ? 'text-[12px]' : 'text-[14px]';
  const labelTextClass = isCompact ? 'text-[11px] py-1' : 'text-[12px] py-3';

  return (
    <motion.div
      className="bg-white rounded-xl shadow-sm border border-slate-100 flex flex-col h-full relative"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex flex-col min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-slate-800 break-words min-w-0">
            Risk Heatmap (Month vs Severity)
          </h3>
          <span className="text-[11px] text-slate-400 font-semibold mt-0.5">
            {(selectedYear && selectedYear !== currentYear) ? `Year ${selectedYear}` : getPeriodLabel(timeFilter, isSubscriptionOnly, subscriptionStartDate)}
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          <div className="relative flex-shrink-0">
            <MdInfo 
              className="h-4 w-4 text-slate-300 hover:text-blue-500 cursor-help transition-colors"
              onMouseEnter={() => setShowInfo(true)}
              onMouseLeave={() => setShowInfo(false)}
            />
            <AnimatePresence>
              {showInfo && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  className="absolute left-0 top-full mt-2 z-50 w-64 bg-[#010D2C] text-white p-3 rounded-xl text-[11px] font-medium shadow-xl pointer-events-none leading-relaxed"
                >
                  A timeline breakdown of vulnerabilities by severity (grouped by weeks or months depending on the selected timeframe). Darker colors indicate higher risk density for that specific period (includes accepts risk/non-actionable reports).
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {(!selectedYear || selectedYear === currentYear) && (
            <ChartFilter
              active={timeFilter}
              onChange={setTimeFilter}
              isSubscriptionOnly={isSubscriptionOnly}
              subscriptionStartDate={subscriptionStartDate}
              supportsAllTime={!selectedYear}
              selectedYear={selectedYear}
            />
          )}
        </div>
      </div>

      {/* Heatmap Grid */}
      <div className="p-4 overflow-y-auto flex-1 custom-scrollbar">
        <AnimatePresence>
          {isLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 backdrop-blur-sm rounded-b-xl"
            >
              <FiLoader className="w-8 h-8 text-blue-500 animate-spin" />
            </motion.div>
          )}
        </AnimatePresence>

         <table className={`w-full border-separate border-spacing-x-3 ${spacingClass}`}>
          <thead>
            <tr>
              <th className="w-28"></th>
              {(['Critical', 'High', 'Medium', 'Low', 'Info'] as const).map(sev => {
                const initial = sev === 'Critical' ? 'C' : sev === 'High' ? 'H' : sev === 'Medium' ? 'M' : sev === 'Low' ? 'L' : 'I';
                return (
                  <th key={sev} className={`text-center py-1 text-[13px] font-black uppercase tracking-wider ${getHeaderColor(sev)}`}>
                    {initial}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {localData && localData.length > 0 ? (
              localData.map((row, idx) => (
                <tr key={idx} className="group">
                  <td className={`text-left font-bold text-slate-400 uppercase tracking-widest ${labelTextClass}`}>
                    {row.month}
                  </td>
                  {(['critical', 'high', 'medium', 'low', 'info'] as const).map(sev => (
                    <td key={sev} className="text-center p-0">
                      <motion.div
                        className={`w-full rounded-md font-black transition-all shadow-sm border border-transparent hover:border-slate-200 ${cellPaddingClass} ${cellTextClass} ${getBgColor(sev, (row as any)[sev])}`}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: idx * 0.05 }}
                      >
                        {(row as any)[sev] || 0}
                      </motion.div>
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="py-20 text-center text-slate-400 italic text-sm">
                  No risk data available for this period.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="mt-auto px-6 py-4 border-t border-slate-50 flex items-center justify-center gap-5 flex-wrap">
        <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider">
          <span className="text-rose-500 bg-rose-50 border border-rose-100 px-1.5 py-0.5 rounded">C</span>
          <span className="text-slate-400 font-bold">Critical</span>
        </div>
        <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider">
          <span className="text-orange-500 bg-orange-50 border border-orange-100 px-1.5 py-0.5 rounded">H</span>
          <span className="text-slate-400 font-bold">High</span>
        </div>
        <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider">
          <span className="text-amber-500 bg-amber-50 border border-amber-100 px-1.5 py-0.5 rounded">M</span>
          <span className="text-slate-400 font-bold">Medium</span>
        </div>
        <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider">
          <span className="text-emerald-500 bg-emerald-50 border border-emerald-100 px-1.5 py-0.5 rounded">L</span>
          <span className="text-slate-400 font-bold">Low</span>
        </div>
        <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider">
          <span className="text-slate-500 bg-slate-50 border border-slate-100 px-1.5 py-0.5 rounded">I</span>
          <span className="text-slate-400 font-bold">Info</span>
        </div>
      </div>
    </motion.div>
  );
};

export default React.memo(RiskHeatmap);

